import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import {
  billingApi,
  licenseApi,
  type BillingCycle,
  type BillingTier,
} from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface PlanOption {
  tier:        BillingTier;
  cycle:       BillingCycle;
  label:       string;
  priceLine:   string;
  sublabel:    string;
}

const PLAN_OPTIONS: PlanOption[] = [
  { tier: 'standard', cycle: 'monthly', label: 'Standard · Monthly', priceLine: '$25 / user / month', sublabel: 'Core CMMS — work orders, PM, parts, history' },
  { tier: 'standard', cycle: 'annual',  label: 'Standard · Annual',  priceLine: '$240 / user / year', sublabel: 'Save ~20% vs monthly' },
  { tier: 'pro',      cycle: 'monthly', label: 'Pro · Monthly',      priceLine: '$40 / user / month', sublabel: 'Adds meter-based PMs + KPI reports (MTBF, MTTR, PM compliance)' },
  { tier: 'pro',      cycle: 'annual',  label: 'Pro · Annual',       priceLine: '$420 / user / year', sublabel: 'Save ~13% vs monthly' },
];

export function BillingSettingsPage() {
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [error,  setError]  = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const status = params.get('status');

  // Handle Stripe return: show banner, refresh status, then clean the URL.
  useEffect(() => {
    if (status !== 'success') return;
    setShowSuccess(true);
    qc.invalidateQueries({ queryKey: ['license-status'] });
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      next.delete('status');
      setParams(next, { replace: true });
    }, 200);
    return () => clearTimeout(t);
  }, [status, qc, params, setParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['license-status'],
    queryFn:  licenseApi.status,
  });

  const checkout = useMutation({
    mutationFn: ({ tier, cycle }: { tier: BillingTier; cycle: BillingCycle }) =>
      billingApi.checkout(tier, cycle),
    onSuccess: (res) => { window.location.href = res.url; },
    onError:   (err: Error) => setError(err.message),
  });

  const portal = useMutation({
    mutationFn: () => billingApi.portal(),
    onSuccess: (res) => { window.location.href = res.url; },
    onError:   (err: Error) => setError(err.message),
  });

  if (!user) return null;
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-xl mx-auto bg-white rounded-xl border border-slate-200 p-6 text-sm text-slate-700">
          Billing settings are admin-only.
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return <div className="min-h-screen bg-slate-50 p-6 text-slate-500">Loading...</div>;
  }

  const isCloud   = data.plan === 'cloud';
  const hasActive = !!data.has_active_sub;
  const cancelled = status === 'cancelled';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 sm:px-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-600"
        >
          <ArrowLeft size={16} />
          Back to app
        </Link>
      </header>

      <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <h1 className="text-xl font-bold text-slate-900 mb-6">Billing</h1>

      {showSuccess && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <span className="font-semibold">Subscription active.</span>{' '}
          You now have {(data.tier ?? '').toString().toUpperCase()} access.
        </div>
      )}

      {cancelled && !showSuccess && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Checkout cancelled — no changes were made.
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── Active cloud subscription ─────────────────────────────────────── */}
      {isCloud && hasActive ? (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div>
              <p className="text-sm text-slate-500 mb-1">Current Plan</p>
              <p className="text-lg font-semibold text-slate-900">
                Cloud · {(data.tier ?? 'standard').toString().replace(/^./, c => c.toUpperCase())}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {data.tier === 'pro' ? '$40 / user / month' : '$25 / user / month'}
              </p>
            </div>
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
              Active
            </span>
          </div>

          {data.billing_email && (
            <div className="mt-4 text-sm">
              <span className="text-slate-500">Billing email: </span>
              <span className="text-slate-800 font-medium">{data.billing_email}</span>
            </div>
          )}

          <button
            onClick={() => portal.mutate()}
            disabled={portal.isPending}
            className="mt-5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold py-2 px-5 rounded-lg text-sm transition-colors"
          >
            {portal.isPending ? 'Opening…' : 'Manage Billing'}
          </button>
          <p className="mt-3 text-xs text-slate-500">
            Opens the secure Stripe portal — update card, change plan, download invoices, or cancel.
          </p>
        </div>
      ) : (
        // ── No active subscription ──────────────────────────────────────────
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
            <p className="text-sm text-slate-500 mb-1">Current Plan</p>
            <p className="text-lg font-semibold text-slate-900">
              {data.plan === 'demo' ? 'Free Demo' :
               data.plan === 'onprem' ? 'On-Premise' :
               data.plan === 'expired' ? 'Expired' :
               data.plan === 'cloud' ? 'Cloud (no active subscription)' :
               data.plan}
            </p>
            {data.expiresAt && (
              <p className="mt-1 text-sm text-slate-500">
                {data.plan === 'demo' ? 'Demo ends ' : 'Expires '}
                {new Date(data.expiresAt).toLocaleDateString()}
              </p>
            )}
            <p className="mt-3 text-sm text-slate-600">
              Pick a plan below to subscribe and unlock full cloud access.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {PLAN_OPTIONS.map(opt => {
              const isPending = checkout.isPending
                && checkout.variables?.tier  === opt.tier
                && checkout.variables?.cycle === opt.cycle;
              return (
                <div
                  key={`${opt.tier}-${opt.cycle}`}
                  className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col"
                >
                  <p className="font-semibold text-slate-900">{opt.label}</p>
                  <p className="text-sm text-slate-700 mt-1">{opt.priceLine}</p>
                  <p className="text-xs text-slate-500 mt-1 flex-1">{opt.sublabel}</p>
                  <button
                    onClick={() => { setError(''); checkout.mutate({ tier: opt.tier, cycle: opt.cycle }); }}
                    disabled={checkout.isPending}
                    className={
                      'mt-4 font-semibold py-2 px-4 rounded-lg text-sm transition-colors disabled:opacity-50 ' +
                      (opt.tier === 'pro'
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-brand-600 hover:bg-brand-700 text-white')
                    }
                  >
                    {isPending ? 'Redirecting…' : 'Subscribe'}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-slate-500">
            Subscriptions are managed by Stripe. You can cancel any time from the billing portal.
          </p>
        </>
      )}
      </div>
    </div>
  );
}
